import type { ReactNode } from 'react';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import { Sugestoes } from './campos';
import type { ItemBase, ItemTexto } from '../manual-types';

export interface ColunaLista<T> {
  chave: keyof T & string;
  rotulo: string;
  tipo?: 'texto' | 'area' | 'selecao';
  opcoes?: { valor: string; rotulo: string }[];
  placeholder?: string;
  /** Fração da linha, em unidades de grid (padrão `1fr`). */
  largura?: string;
}

interface ListaEditavelProps<T extends ItemBase> {
  titulo?: string;
  descricao?: string;
  itens: T[];
  colunas: ColunaLista<T>[];
  rotuloAdicionar?: string;
  vazio?: string;
  numerar?: boolean;
  sugestoes?: string[];
  tituloSugestoes?: string;
  aoAdicionar: () => void;
  aoAtualizar: (id: string, patch: Partial<T>) => void;
  aoRemover: (id: string) => void;
  aoMover?: (id: string, direcao: -1 | 1) => void;
  aoEscolherSugestao?: (valor: string) => void;
  rodape?: ReactNode;
  detalhe?: (item: T, indice: number) => ReactNode;
}

export function ListaEditavel<T extends ItemBase>({
  titulo,
  descricao,
  itens,
  colunas,
  rotuloAdicionar = 'Adicionar item',
  vazio = 'Nenhum item cadastrado.',
  numerar = true,
  sugestoes,
  tituloSugestoes,
  aoAdicionar,
  aoAtualizar,
  aoRemover,
  aoMover,
  aoEscolherSugestao,
  rodape,
  detalhe,
}: ListaEditavelProps<T>) {
  const template = `${numerar ? '26px ' : ''}${colunas.map((coluna) => coluna.largura ?? '1fr').join(' ')} auto`;

  return (
    <div className="manual-lista">
      {(titulo || descricao) && (
        <div className="manual-lista-cabecalho">
          {titulo && <strong>{titulo}</strong>}
          {descricao && <p>{descricao}</p>}
        </div>
      )}

      {itens.length > 0 && (
        <div className="manual-lista-titulos" style={{ gridTemplateColumns: template }}>
          {numerar && <span />}
          {colunas.map((coluna) => <span key={coluna.chave}>{coluna.rotulo}</span>)}
          <span />
        </div>
      )}

      {itens.length === 0 && <p className="manual-lista-vazia">{vazio}</p>}

      {itens.map((item, indice) => (
        <div key={item.id} className="manual-lista-item">
          <div className="manual-lista-linha" style={{ gridTemplateColumns: template }}>
            {numerar && <span className="manual-lista-numero">{indice + 1}</span>}
            {colunas.map((coluna) => {
              const valor = String((item as Record<string, unknown>)[coluna.chave] ?? '');
              if (coluna.tipo === 'area') {
                return (
                  <textarea
                    key={coluna.chave}
                    rows={2}
                    value={valor}
                    placeholder={coluna.placeholder}
                    aria-label={coluna.rotulo}
                    onChange={(evento) => aoAtualizar(item.id, { [coluna.chave]: evento.target.value } as Partial<T>)}
                  />
                );
              }
              if (coluna.tipo === 'selecao') {
                return (
                  <select
                    key={coluna.chave}
                    value={valor}
                    aria-label={coluna.rotulo}
                    onChange={(evento) => aoAtualizar(item.id, { [coluna.chave]: evento.target.value } as Partial<T>)}
                  >
                    <option value="">—</option>
                    {(coluna.opcoes ?? []).map((opcao) => (
                      <option key={opcao.valor} value={opcao.valor}>{opcao.rotulo}</option>
                    ))}
                  </select>
                );
              }
              return (
                <input
                  key={coluna.chave}
                  value={valor}
                  placeholder={coluna.placeholder}
                  aria-label={coluna.rotulo}
                  onChange={(evento) => aoAtualizar(item.id, { [coluna.chave]: evento.target.value } as Partial<T>)}
                />
              );
            })}
            <div className="manual-lista-acoes">
              {aoMover && (
                <>
                  <button type="button" title="Mover para cima" disabled={indice === 0} onClick={() => aoMover(item.id, -1)}>
                    <ArrowUp />
                  </button>
                  <button type="button" title="Mover para baixo" disabled={indice === itens.length - 1} onClick={() => aoMover(item.id, 1)}>
                    <ArrowDown />
                  </button>
                </>
              )}
              <button type="button" title="Remover" className="is-danger" onClick={() => aoRemover(item.id)}>
                <Trash2 />
              </button>
            </div>
          </div>
          {detalhe?.(item, indice)}
        </div>
      ))}

      <div className="manual-lista-rodape">
        <button type="button" className="manual-ghost-button" onClick={aoAdicionar}>
          <Plus />{rotuloAdicionar}
        </button>
        {rodape}
      </div>

      {sugestoes && aoEscolherSugestao && (
        <Sugestoes titulo={tituloSugestoes} itens={sugestoes} aoEscolher={aoEscolherSugestao} />
      )}
    </div>
  );
}

/** Lista de itens com um único campo textual (precauções, EPIs, requisitos...). */
export function ListaTextos({
  titulo,
  descricao,
  itens,
  placeholder,
  rotuloAdicionar,
  vazio,
  sugestoes,
  multilinha,
  aoAdicionar,
  aoAtualizar,
  aoRemover,
  aoMover,
  aoEscolherSugestao,
}: {
  titulo?: string;
  descricao?: string;
  itens: ItemTexto[];
  placeholder?: string;
  rotuloAdicionar?: string;
  vazio?: string;
  sugestoes?: string[];
  multilinha?: boolean;
  aoAdicionar: () => void;
  aoAtualizar: (id: string, patch: Partial<ItemTexto>) => void;
  aoRemover: (id: string) => void;
  aoMover?: (id: string, direcao: -1 | 1) => void;
  aoEscolherSugestao?: (valor: string) => void;
}) {
  return (
    <ListaEditavel<ItemTexto>
      titulo={titulo}
      descricao={descricao}
      itens={itens}
      colunas={[{ chave: 'texto', rotulo: 'Descrição', placeholder, tipo: multilinha ? 'area' : 'texto' }]}
      rotuloAdicionar={rotuloAdicionar}
      vazio={vazio}
      sugestoes={sugestoes}
      aoAdicionar={aoAdicionar}
      aoAtualizar={aoAtualizar}
      aoRemover={aoRemover}
      aoMover={aoMover}
      aoEscolherSugestao={aoEscolherSugestao}
    />
  );
}

export type { ItemBase };
